import { isErrorLike } from "@apollo/client/errors";
import { find, forEach, isEmpty } from "lodash";
import { all, call, put, select, take } from "redux-saga/effects";

import {
  fetchBooking,
  type UpdateBookingResult,
  updateBooking,
} from "../../../common/api";
import {
  type GoodsValidationError,
  getIsGoodsItemEdited,
} from "../../../common/goods/goodsUtils";
import { Errors } from "../../../common/texts";
import { trackEvent } from "../../../common/tracking/trackerService";
import { transformToUpdateBookingFormState } from "../../../common/update-information/utils/transformToUpdateBookingFormState";
import { transformToInput } from "../../../use-cases/manage/details/utils/transformToInput";
import type {
  BookingValidationErrors,
  UpdateFormState,
} from "../updateBookingReducer";
import {
  finishSubmit,
  setRegistrationNumberAlert,
  startSubmit,
  type UpdateFormRequestSubmitAction,
  type UpdateFormUpdateFinishedAction,
} from "../updateFormActions";
import { initializeAllUpdateFormsWithBooking } from "../updateFormActionsHelpers";
import {
  type FormInstanceId,
  getFormIsEdited,
  getFormIsSubmitting,
  getFormIsUpdating,
  getUpdateFormState,
} from "../updateFormReducer";

function validate(state: UpdateFormState): BookingValidationErrors | null {
  const validationErrors: BookingValidationErrors = {};

  if (!state.editedFormState?.sailing) {
    validationErrors.sailing = Errors.SelectDepartureTime;
  }

  if (!state.editedFormState?.departureDate) {
    validationErrors.departureDate = Errors.SelectDepartureDate;
  }

  if (!isEmpty(state.editedFormState?.goods) && !state.editedFormState?.empty) {
    forEach(state.editedFormState?.goods, (item) => {
      const initialGoodsItem = find(
        state.initialFormState?.goods,
        (g) => g.id === item.id,
      );

      if (initialGoodsItem && !getIsGoodsItemEdited(initialGoodsItem, item)) {
        return;
      }

      const validationErrorsForItem: GoodsValidationError =
        validationErrors.goods?.[item.id] ?? {};

      if (!item.goodsType) {
        validationErrorsForItem.type = Errors.SelectGoodsType;
      }

      if (!item.description.trim()) {
        validationErrorsForItem.description = Errors.EnterGoodsDescription;
      }

      if (item.weight == null) {
        validationErrorsForItem.weight = Errors.EnterGoodsWeight;
      }

      if (!isEmpty(validationErrorsForItem)) {
        validationErrors.goods = {
          ...validationErrors.goods,
          [item.id]: validationErrorsForItem,
        };
      }
    });
  }

  if (isEmpty(validationErrors)) {
    return null;
  }

  return validationErrors;
}

function* submitUpdateBooking(
  { bookingNo, formId }: FormInstanceId,
  state: UpdateFormState,
) {
  const registrationNumberAlert = state.registrationNumberAlert;
  yield put(startSubmit({ bookingNo, formId }));

  const validationErrors = validate(state);

  const trackAction = "Save result";

  if (validationErrors != null) {
    trackEvent({
      category: formId,
      action: trackAction,
      label: "Error - Client validation",
    });

    yield put(
      finishSubmit(
        { bookingNo, formId },
        {
          result: {
            booking: null,
            bookingOutOfDate: false,
            messages: [],
            errors: [],
            success: false,
            waitlistPossible: false,
            warnings: [],
          },
          genericError: Errors.SaveFailedReviewChanges,
        },
        validationErrors,
      ),
    );
  } else {
    try {
      const result: UpdateBookingResult = yield call(
        updateBooking,
        transformToInput(state.editedFormState!),
        formId === "Details",
      );

      if (result.errors.length > 0) {
        trackEvent({
          category: formId,
          action: trackAction,
          label: "Error - Server validation",
        });

        yield put(
          finishSubmit(
            { bookingNo, formId },
            {
              genericError: Errors.SomethingWentWrong,
            },
            {},
          ),
        );
      } else {
        const updateBookingResult = result.result!;
        if (updateBookingResult.success) {
          yield all(
            initializeAllUpdateFormsWithBooking(
              updateBookingResult.booking!,
            ).map((action) => put(action)),
          );
          if (
            registrationNumberAlert &&
            !registrationNumberAlert.appliedArrivalNotepad
          ) {
            yield put(
              setRegistrationNumberAlert(
                { bookingNo, formId },
                registrationNumberAlert,
              ),
            );
          }
        }

        if (updateBookingResult.bookingOutOfDate) {
          const fetchBookingResult: Awaited<ReturnType<typeof fetchBooking>> =
            yield call(fetchBooking, bookingNo);

          const booking = fetchBookingResult.success
            ? fetchBookingResult.value
            : null;

          if (booking) {
            const updatedFormState = transformToUpdateBookingFormState(
              booking,
              {
                canBeEmpty: formId === "Details",
              },
            );

            trackEvent({
              category: formId,
              action: trackAction,
              label: "Error - BookingOutOfDate",
            });

            yield put(
              finishSubmit(
                { bookingNo, formId },
                {
                  bookingOutOfDate: true,
                  result: updateBookingResult,
                },
                {},
                booking,
                updatedFormState,
              ),
            );
          }
        } else {
          trackEvent({
            category: formId,
            action: trackAction,
            label: updateBookingResult.success ? "Success" : "Error",
          });
          yield put(
            finishSubmit(
              { bookingNo, formId },
              {
                result: updateBookingResult,
              },
              {},
            ),
          );
        }
      }
    } catch (e) {
      trackEvent({
        category: formId,
        action: trackAction,
        label: "Error",
      });
      yield put(
        finishSubmit(
          { bookingNo, formId },
          {
            genericError: isErrorLike(e)
              ? e.message
              : Errors.SomethingWentWrong,
          },
          {},
        ),
      );
    }
  }
}

function* submitFormIfChanged({ bookingNo, formId }: FormInstanceId) {
  const edited: ReturnType<typeof getFormIsEdited> = yield select(
    getFormIsEdited,
    {
      bookingNo,
      formId,
    },
  );

  if (edited) {
    trackEvent({
      category: formId,
      action: "Save",
    });
    const state: ReturnType<typeof getUpdateFormState> = yield select(
      getUpdateFormState,
      {
        bookingNo,
        formId,
      },
    );
    if (state) {
      yield submitUpdateBooking({ bookingNo, formId }, state);
    }
  }
}

export function* handleWaitForSubmit({
  payload: {
    id: { formId, bookingNo },
  },
}: UpdateFormRequestSubmitAction) {
  const updating: ReturnType<typeof getFormIsUpdating> = yield select(
    getFormIsUpdating,
    {
      formId,
      bookingNo,
    },
  );
  const submitting: ReturnType<typeof getFormIsSubmitting> = yield select(
    getFormIsSubmitting,
    {
      formId,
      bookingNo,
    },
  );
  if (!updating) {
    if (!submitting) {
      yield submitFormIfChanged({ formId, bookingNo });
    }
  } else {
    while (true) {
      const { payload }: UpdateFormUpdateFinishedAction = yield take(
        "UPDATE_FORM:UPDATE_FINISHED",
      );
      if (bookingNo === payload.id.bookingNo && formId === payload.id.formId) {
        const formIsUpdating: ReturnType<typeof getFormIsUpdating> =
          yield select(getFormIsUpdating, { formId, bookingNo });
        if (!formIsUpdating) {
          yield submitFormIfChanged({ formId, bookingNo });
          return;
        }
      }
    }
  }
}
